import { Injectable } from '@angular/core';
import { Observable, BehaviorSubject } from 'rxjs';
import { map } from 'rxjs/operators';

import { MySocket } from '@app/core/services/socketio.service';
import { UserStorageService } from '@app/core/services/user-storage.service';
import { User } from '../models/user';
import { ResponseError } from '../models/error';
import { UserSession } from '../models/user-session';

@Injectable({
  providedIn: 'root'
})
export class AuthenticationService {
  public onLogin$ = this.socket.fromEvent<UserSession>('authorization/on-login').pipe(map(userSession => {
    // login successful if there's a jwt token in the response
    if (userSession) {
      // store user details and jwt token in local storage to keep user logged in between page refreshes
      this.userStorageService.currentUserSession = userSession;
      this.loggedIn.next(true);
    }

    return userSession;
  }));

  public onLoginError$ = this.socket.fromEvent<ResponseError>('authorization/on-login-error');
  public onGetCurrentUser$ = this.socket.fromEvent<User>('authorization/on-get-current-user');
  public onSetCurrentUser$ = this.socket.fromEvent<User>('authorization/on-set-current-user').pipe(map(user => {
    if (user) {
      const updatedUser = {...this.userStorageService.currentUser, ...user};
      this.userStorageService.currentUser = updatedUser;
    }

    return user;
  }));
  public onGetIsAdmin$ = this.socket.fromEvent<User>('authorization/on-get-is-admin');

  public onRegister$ = this.socket.fromEvent<User>('authorization/on-register');
  public onRegisterError$ = this.socket.fromEvent<ResponseError>('authorization/on-register-error');

  private loggedIn = new BehaviorSubject<boolean>(this.userStorageService.isCurrentUserValid());

  constructor(
    private socket: MySocket,
    private userStorageService: UserStorageService) {
  }

  login(username: string, password: string) {
    this.socket.emit('authorization/do-login', { username: username, password: password });
  }

  tokenLogin(token: string) {
    this.socket.emit('authorization/do-token-login', { token: token});
  }

  register(username: string, password: string, firstName: string, lastName: string) {
    this.socket.emit('authorization/do-register', { username: username, password: password, firstName: firstName, lastName: lastName });
  }

  isAdmin() {
    this.socket.emit('authorization/do-get-is-admin', {});
  }

  logOut(): void {
    // remove user from local storage to log user out
    this.loggedIn.next(false);
    this.userStorageService.logOut();
  }

  get isLoggedIn(): Observable<boolean> {
    return this.loggedIn.asObservable();
  }

  updateCurrentUser(updateData: object) {
    this.socket.emit('authorization/do-set-current-user', updateData);
  }

  getCurrentUser() {
    this.socket.emit('authorization/do-get-current-user');
  }
}
